/**
 * AR URL Generator Utility
 * Dynamically generates AR viewer URLs for both local and cloud environments
 */

interface ARUrlConfig {
  modelUrl: string;
  thumbnailUrl?: string;
}

/**
 * Get the appropriate host and protocol for AR viewer URLs
 */
export function getARViewerHost(): string {
  try {
    const environment = import.meta.env.VITE_ENVIRONMENT || 'development';
    const currentHost = window.location.hostname;
    const currentProtocol = window.location.protocol;

    // Detect if we're in a cloud/production environment by checking the hostname
    const isCloudEnvironment = !currentHost.includes('localhost') &&
      !currentHost.includes('127.0.0.1') &&
      !currentHost.includes('192.168.') &&
      !currentHost.includes('10.') &&
      !currentHost.includes('172.');

    const isProduction = environment === 'production' || isCloudEnvironment;

    if (isProduction) {
      // Production/Cloud: Use the current domain with same protocol
      return `${currentProtocol}//${currentHost}`;
    } else {
      // Development: Check for custom AR host IP or use current location
      const arHostIP = import.meta.env.VITE_AR_HOST_IP;

      if (arHostIP && arHostIP !== 'localhost' && arHostIP !== '127.0.0.1') {
        // Custom AR host IP specified (for local network testing)
        // Use HTTP for local network IPs, but keep current protocol for localhost
        const protocol = arHostIP.startsWith('192.168.') || arHostIP.startsWith('10.') || arHostIP.startsWith('172.')
          ? 'http:'
          : window.location.protocol;
        const port = window.location.port ? `:${window.location.port}` : '';
        return `${protocol}//${arHostIP}${port}`;
      } else {
        // Use current location (default for local development)
        return `${window.location.protocol}//${window.location.host}`;
      }
    }
  } catch (error) {
    console.warn('⚠️ Error in getARViewerHost, falling back to current location:', error);
    // Fallback to current location if anything goes wrong
    return `${window.location.protocol}//${window.location.host}`;
  }
}

/**
 * Generate AR viewer URL with proper host detection
 */
export function generateARViewerUrl(config: ARUrlConfig): string {
  const arHost = getARViewerHost();
  const { modelUrl, thumbnailUrl = '' } = config;

  // Construct the AR viewer URL
  const viewerUrl = new URL('/ar-viewer.html', arHost);
  viewerUrl.searchParams.set('model', modelUrl);

  if (thumbnailUrl) {
    viewerUrl.searchParams.set('thumbnail', thumbnailUrl);
  }

  return viewerUrl.toString();
}

/**
 * Get the backend API URL for model endpoints
 */
export function getModelApiUrl(): string {
  try {
    const environment = import.meta.env.VITE_ENVIRONMENT || 'development';
    const currentHost = window.location.hostname;
    const currentProtocol = window.location.protocol;

    // Detect if we're in a cloud/production environment by checking the hostname
    const isCloudEnvironment = !currentHost.includes('localhost') &&
      !currentHost.includes('127.0.0.1') &&
      !currentHost.includes('192.168.') &&
      !currentHost.includes('10.') &&
      !currentHost.includes('172.');

    const isProduction = environment === 'production' || isCloudEnvironment;

    // Debug environment variables
    console.log('🔍 getModelApiUrl - Environment Variables:', {
      VITE_API_URL: import.meta.env.VITE_API_URL,
      VITE_AR_HOST_IP: import.meta.env.VITE_AR_HOST_IP,
      VITE_ENVIRONMENT: environment,
      currentHost,
      currentProtocol,
      isCloudEnvironment,
      isProduction
    });

    if (isProduction) {
      // Production/Cloud: Use the current domain with same protocol for API calls
      const apiUrl = `${currentProtocol}//${currentHost}`;
      console.log('🔍 Using production/cloud API URL:', apiUrl);
      return apiUrl;
    } else {
      // Development: For AR functionality, prioritize network IP over localhost for mobile compatibility
      const arHostIP = import.meta.env.VITE_AR_HOST_IP;
      if (arHostIP && arHostIP !== 'localhost' && arHostIP !== '127.0.0.1') {
        // Use the same IP for API calls to ensure mobile devices can access it
        const apiUrl = `http://${arHostIP}:8000`;
        console.log('🔍 Using AR Host IP for API:', apiUrl);
        return apiUrl;
      }

      // Use VITE_API_URL if available (this will be set in cloud environments)
      const viteApiUrl = import.meta.env.VITE_API_URL;
      if (viteApiUrl && !viteApiUrl.includes('localhost') && !viteApiUrl.includes('127.0.0.1')) {
        console.log('🔍 Using VITE_API_URL:', viteApiUrl);
        return viteApiUrl;
      }

      // If we have VITE_API_URL but it's localhost, still use it (for development)
      if (viteApiUrl) {
        console.log('🔍 Using VITE_API_URL (localhost):', viteApiUrl);
        return viteApiUrl;
      }

      // Fallback to localhost for development (but warn about mobile compatibility)
      console.warn('⚠️ Using localhost - AR will not work on mobile devices');
      return 'http://localhost:8000';
    }
  } catch (error) {
    console.warn('⚠️ Error in getModelApiUrl, falling back to current location:', error);
    // Fallback to current location if anything goes wrong
    const protocol = window.location.protocol;
    const host = window.location.host;
    return `${protocol}//${host}`;
  }
}

/**
 * Generate model URL for AR viewer

*/
export function generateModelUrl(mediaId: string, useMock: boolean = false): string {
  const apiUrl = getModelApiUrl();

  if (useMock) {
    return `${apiUrl}/api/v1/mock/mock-3d-model`;
  } else {
    return `${apiUrl}/api/v1/media/${mediaId}/3d-model`;
  }
}

/**
 * Complete AR URL generation with environment detection
 */
export function generateCompleteARUrl(mediaId: string, thumbnailUrl?: string, useMock: boolean = false): string {
  const modelUrl = generateModelUrl(mediaId, useMock);

  return generateARViewerUrl({
    modelUrl,
    thumbnailUrl
  });
}

/**
 * Debug information for AR URL generation
 */
export function getARUrlDebugInfo() {
  return {
    environment: import.meta.env.VITE_ENVIRONMENT || 'development',
    arHostIP: import.meta.env.VITE_AR_HOST_IP || 'not set',
    currentHost: window.location.hostname,
    currentPort: window.location.port,
    currentProtocol: window.location.protocol,
    arViewerHost: getARViewerHost(),
    modelApiUrl: getModelApiUrl()
  };
}